import { useState } from 'react';
import {
  Car,
  Plus,
  X,
  CheckSquare,
  Square,
  TrendingUp,
  DollarSign,
  Package,
  Zap,
  FileText,
  ChevronDown,
  ChevronUp,
  Check,
  Eye,
  MousePointer,
  Info,
  Download,
  Printer,
  Copy,
  CheckCircle2,
} from 'lucide-react';
import { calculateDealerOffer } from '../../lib/financingScenarios';
import { ContractPanel } from '../contracts/ContractPanel';
import { Badge } from '../badges/Badge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VehicleStatus = 'Available' | 'Pending Offer' | 'Funded';

interface VehicleEngagement {
  views: number;
  profileClicks: number;
  campaignSelections: number;
}

interface Vehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number;
  mileage: number;
  vin: string;
  imageUrl: string;
  status: VehicleStatus;
  boosted: boolean;
  engagement: VehicleEngagement;
}

interface ProofingVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  imageUrl: string;
  price?: number;
  mileage?: number;
}

type OfferStatus = 'pending' | 'accepted' | 'funded';

interface DealerOffer {
  id: string;
  vehicleName: string;
  imageUrl: string;
  campaignName: string;
  creatorName: string;
  famOffer: number;
  status: OfferStatus;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Sample data
// ---------------------------------------------------------------------------

const SAMPLE_VEHICLES: Vehicle[] = [
  {
    id: '1',
    year: 2020,
    make: 'Toyota',
    model: 'Camry',
    price: 18500,
    mileage: 42000,
    vin: '4T1B11HK5LU123456',
    imageUrl:
      'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&q=80',
    status: 'Available',
    boosted: true,
    engagement: { views: 847, profileClicks: 234, campaignSelections: 12 },
  },
  {
    id: '2',
    year: 2019,
    make: 'Honda',
    model: 'Accord',
    price: 16900,
    mileage: 58000,
    vin: '1HGCV1F3XKA654321',
    imageUrl:
      'https://images.unsplash.com/photo-1618843479619-f3d0d81e4d10?w=600&q=80',
    status: 'Pending Offer',
    boosted: false,
    engagement: { views: 623, profileClicks: 189, campaignSelections: 8 },
  },
  {
    id: '3',
    year: 2021,
    make: 'Ford',
    model: 'F-150',
    price: 32000,
    mileage: 28000,
    vin: '1FTFW1E88MFA11111',
    imageUrl:
      'https://images.unsplash.com/photo-1605893477799-b99e3b8b93fe?w=600&q=80',
    status: 'Available',
    boosted: false,
    engagement: { views: 1203, profileClicks: 412, campaignSelections: 19 },
  },
  {
    id: '4',
    year: 2018,
    make: 'Chevrolet',
    model: 'Malibu',
    price: 13400,
    mileage: 71000,
    vin: '1G1ZD5ST5JF222222',
    imageUrl:
      'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&q=80',
    status: 'Funded',
    boosted: false,
    engagement: { views: 321, profileClicks: 87, campaignSelections: 3 },
  },
  {
    id: '5',
    year: 2022,
    make: 'Hyundai',
    model: 'Elantra',
    price: 21500,
    mileage: 14000,
    vin: '5NPLM4AG5NH333333',
    imageUrl:
      'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80',
    status: 'Available',
    boosted: false,
    engagement: { views: 756, profileClicks: 298, campaignSelections: 14 },
  },
  {
    id: '6',
    year: 2017,
    make: 'Nissan',
    model: 'Altima',
    price: 11200,
    mileage: 89000,
    vin: '1N4AL3AP1HC444444',
    imageUrl:
      'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80',
    status: 'Available',
    boosted: false,
    engagement: { views: 445, profileClicks: 156, campaignSelections: 7 },
  },
];

const PROOFING_VEHICLES: ProofingVehicle[] = [
  {
    id: '1',
    year: 2020,
    make: 'Toyota',
    model: 'Camry',
    imageUrl:
      'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&q=80',
  },
  {
    id: '2',
    year: 2019,
    make: 'Honda',
    model: 'Accord',
    imageUrl:
      'https://images.unsplash.com/photo-1618843479619-f3d0d81e4d10?w=600&q=80',
  },
];

const SAMPLE_OFFERS: DealerOffer[] = [
  {
    id: 'o1',
    vehicleName: '2020 Toyota Camry',
    imageUrl: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&q=80',
    campaignName: "Jordan's First Family Car",
    creatorName: 'Jordan S.',
    famOffer: 17575,
    status: 'pending',
    expiresAt: 'in 18 hrs',
  },
  {
    id: 'o2',
    vehicleName: '2019 Honda Accord',
    imageUrl: 'https://images.unsplash.com/photo-1618843479619-f3d0d81e4d10?w=600&q=80',
    campaignName: "Maya's Tesla Model 3",
    creatorName: 'Maya R.',
    famOffer: 15655,
    status: 'accepted',
    expiresAt: 'in 6 hrs',
  },
];

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: VehicleStatus }) {
  const styles: Record<VehicleStatus, string> = {
    Available: 'bg-green-100 text-green-700',
    'Pending Offer': 'bg-amber-100 text-amber-700',
    Funded: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status]}`}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Offer status badge
// ---------------------------------------------------------------------------

function OfferStatusBadge({ status }: { status: OfferStatus }) {
  const styles: Record<OfferStatus, string> = {
    pending: 'bg-amber-100 text-amber-700',
    accepted: 'bg-green-100 text-green-700',
    funded: 'bg-indigo-100 text-indigo-700',
  };
  const labels: Record<OfferStatus, string> = {
    pending: 'Pending',
    accepted: 'Accepted',
    funded: 'Funded',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Proofing checklist modal
// ---------------------------------------------------------------------------

function ProofingModal({
  vehicle,
  onClose,
}: {
  vehicle: ProofingVehicle;
  onClose: () => void;
}) {
  const [checks, setChecks] = useState({
    title: false,
    agreement: false,
    availability: false,
  });
  const [showAgreementContract, setShowAgreementContract] = useState(false);

  const toggle = (key: keyof typeof checks) => {
    if (key === 'agreement' && !checks.agreement) {
      // Open contract signing flow instead of simple toggle
      setShowAgreementContract(true);
      return;
    }
    setChecks((c) => ({ ...c, [key]: !c[key] }));
  };

  const allChecked = Object.values(checks).every(Boolean);

  const offer = vehicle.price
    ? calculateDealerOffer({ price: vehicle.price, year: vehicle.year, mileage: vehicle.mileage })
    : null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">Submit for Funding</h3>
              <p className="text-indigo-200 text-sm">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
            </div>
            <button onClick={onClose} className="text-indigo-200 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-gray-600">
              Complete all items below before submitting this vehicle for funding.
            </p>
            <div className="space-y-3">
              {[
                { key: 'title' as const, label: 'Title document upload' },
                { key: 'agreement' as const, label: 'Sign dealer agreement' },
                { key: 'availability' as const, label: 'Confirm vehicle availability' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
                >
                  {checks[key] ? (
                    <CheckSquare className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${checks[key] ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                    {label}
                  </span>
                  {key === 'agreement' && !checks[key] && (
                    <span className="ml-auto text-xs text-indigo-500 font-medium">Sign →</span>
                  )}
                </button>
              ))}
            </div>
            <button
              disabled={!allChecked}
              onClick={onClose}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition-all ${
                allChecked
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Submit for Funding
            </button>
          </div>
        </div>
      </div>

      {/* Dealer agreement contract */}
      {showAgreementContract && (
        <ContractPanel
          contractType="dealer"
          signerEmail=""
          signerName=""
          fields={[
            { name: 'vehicle', value: `${vehicle.year} ${vehicle.make} ${vehicle.model}` },
            ...(offer ? [{ name: 'cashOffer', value: `$${offer.cashOffer.toLocaleString()}` }] : []),
          ]}
          onComplete={() => {
            setChecks((c) => ({ ...c, agreement: true }));
            setShowAgreementContract(false);
          }}
          onClose={() => setShowAgreementContract(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Add Vehicle inline form
// ---------------------------------------------------------------------------

interface NewVehicleForm {
  year: string;
  make: string;
  model: string;
  price: string;
  mileage: string;
  vin: string;
  imageUrl: string;
}

const EMPTY_FORM: NewVehicleForm = {
  year: '',
  make: '',
  model: '',
  price: '',
  mileage: '',
  vin: '',
  imageUrl: '',
};

function AddVehicleForm({ onAdd, onCancel }: { onAdd: (v: Vehicle) => void; onCancel: () => void }) {
  const [form, setForm] = useState<NewVehicleForm>(EMPTY_FORM);

  const handleSubmit = () => {
    if (!form.year || !form.make || !form.model || !form.price) return;
    onAdd({
      id: Date.now().toString(),
      year: parseInt(form.year),
      make: form.make,
      model: form.model,
      price: parseFloat(form.price),
      mileage: parseFloat(form.mileage) || 0,
      vin: form.vin,
      imageUrl:
        form.imageUrl ||
        'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&q=80',
      status: 'Available',
      boosted: false,
      engagement: { views: 0, profileClicks: 0, campaignSelections: 0 },
    });
    setForm(EMPTY_FORM);
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-indigo-900">New Vehicle</h4>
      <div className="grid grid-cols-3 gap-2">
        {[
          { key: 'year', placeholder: 'Year' },
          { key: 'make', placeholder: 'Make' },
          { key: 'model', placeholder: 'Model' },
        ].map(({ key, placeholder }) => (
          <input
            key={key}
            type="text"
            placeholder={placeholder}
            value={form[key as keyof NewVehicleForm]}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { key: 'price', placeholder: 'Price ($)' },
          { key: 'mileage', placeholder: 'Mileage' },
        ].map(({ key, placeholder }) => (
          <input
            key={key}
            type="number"
            placeholder={placeholder}
            value={form[key as keyof NewVehicleForm]}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        ))}
      </div>
      <input
        type="text"
        placeholder="VIN"
        value={form.vin}
        onChange={(e) => setForm({ ...form, vin: e.target.value })}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <input
        type="url"
        placeholder="Image URL (Unsplash)"
        value={form.imageUrl}
        onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all"
        >
          Add Vehicle
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dealer profile (TODO: replace with auth + DB lookup)
// ---------------------------------------------------------------------------

const DEALER_PROFILE = {
  id: 'dealer-1',
  name: 'Metro Auto Group',
  slug: 'metro-auto',
  phone: '(404) 555-0123',
  city: 'Atlanta',
  state: 'GA',
};

// ---------------------------------------------------------------------------
// Branding section
// ---------------------------------------------------------------------------

function BrandingSection({ dealer }: { dealer: typeof DEALER_PROFILE }) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedButton, setCopiedButton] = useState(false);

  const inventoryUrl = `https://fam.autos/dealer/${dealer.slug}`;
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(inventoryUrl)}&color=000000&bgcolor=ffffff&margin=16`;
  const qrDownloadUrl = `https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=${encodeURIComponent(inventoryUrl)}&color=000000&bgcolor=ffffff&margin=32`;

  const embedIframe = `<iframe src="${inventoryUrl}" width="100%" height="600" frameborder="0" title="${dealer.name} FAM Inventory" style="border-radius:12px;"></iframe>`;
  const embedButton = `<a href="${inventoryUrl}" target="_blank" style="display:inline-flex;align-items:center;gap:8px;background:linear-gradient(to right,#f43f5e,#f59e0b);color:#fff;font-weight:700;padding:12px 24px;border-radius:9999px;text-decoration:none;font-family:sans-serif;">Get Financed with FAM →</a>`;

  const copyText = async (text: string, setter: (v: boolean) => void) => {
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const handlePrintSignage = () => {
    const win = window.open('', '_blank', 'width=800,height=1000');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>FAM Verified Dealer Signage — ${dealer.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #fff; display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 32px; }
    .sign { width: 480px; text-align: center; border: 3px solid #e5e7eb; border-radius: 24px; padding: 48px 40px; }
    .badge { display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #f43f5e, #f59e0b); color: #fff; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; padding: 6px 16px; border-radius: 9999px; margin-bottom: 20px; }
    .dealer { font-size: 22px; font-weight: 800; color: #111827; margin-bottom: 6px; }
    .tagline { font-size: 14px; color: #6b7280; margin-bottom: 32px; }
    .qr-wrap { margin: 0 auto 28px; width: 220px; height: 220px; }
    .qr-wrap img { width: 100%; height: 100%; border-radius: 12px; border: 2px solid #f3f4f6; }
    .instruction { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 8px; }
    .sub { font-size: 13px; color: #9ca3af; }
    .url { margin-top: 20px; font-size: 11px; color: #d1d5db; }
    @media print { body { padding: 0; } .sign { border: 3px solid #e5e7eb; } }
  </style>
</head>
<body>
  <div class="sign">
    <div class="badge">✓ FAM Verified Dealer</div>
    <div class="dealer">${dealer.name}</div>
    <div class="tagline">${dealer.city}, ${dealer.state} · Community-funded car financing</div>
    <div class="qr-wrap">
      <img src="${qrDownloadUrl}" alt="QR code" />
    </div>
    <div class="instruction">Scan to browse &amp; get financed</div>
    <div class="sub">No bank required · Takes 60 seconds to start</div>
    <div class="url">${inventoryUrl}</div>
  </div>
  <script>window.onload = () => window.print();</script>
</body>
</html>`);
    win.document.close();
  };

  return (
    <div className="space-y-6">

      {/* FAM Verified badge + page URL */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Badge preview strip */}
        <div className="bg-gradient-to-r from-gray-950 to-gray-900 px-6 py-8 flex flex-col sm:flex-row items-center gap-6">
          {/* Seal */}
          <Badge type="authorized-dealer" size={88} animate="pulse" />
          <div className="text-center sm:text-left">
            <div className="inline-flex items-center gap-2 bg-rose-500/20 border border-rose-500/30 rounded-full px-3 py-1 mb-2">
              <span className="text-rose-300 text-xs font-bold tracking-wide">FAM VERIFIED DEALER</span>
            </div>
            <p className="text-white text-xl font-extrabold leading-tight">{dealer.name}</p>
            <p className="text-white/50 text-sm">{dealer.city}, {dealer.state}</p>
          </div>
          <div className="sm:ml-auto text-center sm:text-right">
            <p className="text-white/40 text-xs mb-1 uppercase tracking-wide">Inventory page</p>
            <p className="text-white/80 text-sm font-mono bg-white/10 px-3 py-1.5 rounded-lg">
              fam.autos/dealer/{dealer.slug}
            </p>
          </div>
        </div>

        {/* URL + QR row */}
        <div className="p-6 flex flex-col sm:flex-row gap-6">
          {/* QR code */}
          <div className="flex-shrink-0 flex flex-col items-center gap-3">
            <img
              src={qrApiUrl}
              alt="QR code for dealer inventory page"
              className="w-40 h-40 rounded-xl border border-gray-100 shadow-sm"
            />
            <div className="flex gap-2">
              <a
                href={qrDownloadUrl}
                download={`fam-qr-${dealer.slug}.png`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download QR
              </a>
              <button
                onClick={handlePrintSignage}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
                Print Sign
              </button>
            </div>
          </div>

          {/* URL + actions */}
          <div className="flex-1 space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Your inventory page link
              </label>
              <div className="flex gap-2">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 font-mono truncate">
                  {inventoryUrl}
                </div>
                <button
                  onClick={() => copyText(inventoryUrl, setCopiedUrl)}
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2.5 rounded-lg transition-colors flex-shrink-0"
                >
                  {copiedUrl ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedUrl ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Share this link on social, in your email signature, or text it to walk-ins.
              </p>
            </div>

            <a
              href={inventoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
            >
              Preview your inventory page →
            </a>
          </div>
        </div>
      </div>

      {/* Web embed assets */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
        <div>
          <h3 className="font-bold text-gray-900 text-base mb-0.5">Web Assets</h3>
          <p className="text-sm text-gray-500">Embed on your dealership website to drive financing leads.</p>
        </div>

        {/* Iframe embed */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Inventory iframe — full page embed
            </label>
            <button
              onClick={() => copyText(embedIframe, setCopiedEmbed)}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {copiedEmbed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedEmbed ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
            {embedIframe}
          </pre>
        </div>

        {/* CTA button */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              "Get Financed with FAM" button
            </label>
            <button
              onClick={() => copyText(embedButton, setCopiedButton)}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {copiedButton ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedButton ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
            {embedButton}
          </pre>
          {/* Live preview */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-gray-400">Preview:</span>
            <a
              href={inventoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white font-bold py-2 px-5 rounded-full text-sm transition-all hover:scale-105"
            >
              Get Financed with FAM →
            </a>
          </div>
        </div>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

type Tab = 'inventory' | 'offers' | 'proofing' | 'earnings' | 'branding';

export function DealerDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('inventory');
  const [vehicles, setVehicles] = useState<Vehicle[]>(SAMPLE_VEHICLES);
  const [showAddForm, setShowAddForm] = useState(false);
  const [proofingModalVehicle, setProofingModalVehicle] = useState<ProofingVehicle | null>(null);
  const [boostedIds, setBoostedIds] = useState<Set<string>>(new Set(['1']));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acceptedOfferVehicleIds, setAcceptedOfferVehicleIds] = useState<Set<string>>(new Set());

  const tabs: { key: Tab; label: string }[] = [
    { key: 'inventory', label: 'Inventory' },
    { key: 'offers', label: 'Offers' },
    { key: 'proofing', label: 'Proofing' },
    { key: 'earnings', label: 'Earnings' },
    { key: 'branding', label: 'Branding' },
  ];

  const handleAddVehicle = (v: Vehicle) => {
    setVehicles((prev) => [v, ...prev]);
    setShowAddForm(false);
  };

  const handleBoost = (id: string) => {
    setBoostedIds((prev) => new Set([...prev, id]));
  };

  const handleAcceptOffer = (vehicle: Vehicle) => {
    setAcceptedOfferVehicleIds((prev) => new Set([...prev, vehicle.id]));
    setVehicles((prev) =>
      prev.map((v) =>
        v.id === vehicle.id ? { ...v, status: 'Pending Offer' } : v
      )
    );
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dealer Dashboard</h1>
              <p className="text-sm text-gray-500">Manage your FAM inventory and earnings</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 mb-6 w-fit shadow-sm">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === key
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* INVENTORY TAB                                                     */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === 'inventory' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{vehicles.length} vehicles listed</p>
              <button
                onClick={() => setShowAddForm((v) => !v)}
                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add Vehicle
              </button>
            </div>

            {showAddForm && (
              <AddVehicleForm
                onAdd={handleAddVehicle}
                onCancel={() => setShowAddForm(false)}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((vehicle) => {
                const isBoosted = boostedIds.has(vehicle.id);
                const isExpanded = expandedId === vehicle.id;
                const isAccepted = acceptedOfferVehicleIds.has(vehicle.id);
                const offer = calculateDealerOffer(vehicle);
                return (
                  <div
                    key={vehicle.id}
                    className={`bg-white rounded-xl overflow-hidden shadow-sm border transition-all ${
                      isBoosted ? 'border-purple-300 ring-1 ring-purple-200' : 'border-gray-200'
                    }`}
                  >
                    {isBoosted && (
                      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold text-center py-1 flex items-center justify-center gap-1">
                        <Zap className="w-3 h-3" /> FAM Recommended
                      </div>
                    )}
                    <img
                      src={vehicle.imageUrl}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </p>
                          <p className="text-indigo-600 font-bold">
                            ${vehicle.price.toLocaleString()}
                          </p>
                        </div>
                        <StatusChip status={vehicle.status} />
                      </div>

                      {/* FAM Offer section */}
                      <div className="bg-gray-50 rounded-lg p-2.5 mb-3">
                        <div className="flex items-center gap-1 mb-0.5">
                          <p className="text-xs text-gray-500">FAM Offer</p>
                          <div className="relative group">
                            <Info className="w-3 h-3 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 bg-gray-900 text-white text-xs rounded-lg px-2.5 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 leading-relaxed">
                              FAM buys your vehicle outright. Based on book value estimate. Pending VIN verification.
                            </div>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-green-600">
                          ${offer.cashOffer.toLocaleString()}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : vehicle.id)}
                          className="flex-1 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg py-1.5 transition-colors"
                        >
                          Details
                          {isExpanded ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        {!isBoosted && (
                          <button
                            onClick={() => handleBoost(vehicle.id)}
                            className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-purple-600 border border-purple-300 rounded-lg py-1.5 hover:bg-purple-50 transition-colors"
                          >
                            <Zap className="w-3 h-3" /> Boost
                          </button>
                        )}
                        {vehicle.status !== 'Funded' && (
                          isAccepted ? (
                            <span className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg py-1.5">
                              <Check className="w-3 h-3" /> Offer Accepted
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAcceptOffer(vehicle)}
                              className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-green-700 border border-green-300 rounded-lg py-1.5 hover:bg-green-50 transition-colors"
                            >
                              <Check className="w-3 h-3" /> Accept
                            </button>
                          )
                        )}
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-xs text-gray-500">
                          <div className="flex justify-between">
                            <span>Mileage</span>
                            <span className="font-medium text-gray-700">
                              {vehicle.mileage.toLocaleString()} mi
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>VIN</span>
                            <span className="font-medium text-gray-700 font-mono">
                              {vehicle.vin}
                            </span>
                          </div>
                          {/* Engagement stats */}
                          <div className="pt-1 border-t border-gray-100">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3 text-gray-400" />
                                <span className="font-medium text-gray-700">
                                  {vehicle.engagement.views.toLocaleString()}
                                </span>
                                <span>Views</span>
                              </span>
                              <span className="text-gray-300">|</span>
                              <span className="flex items-center gap-1">
                                <MousePointer className="w-3 h-3 text-gray-400" />
                                <span className="font-medium text-gray-700">
                                  {vehicle.engagement.profileClicks.toLocaleString()}
                                </span>
                                <span>Profile clicks</span>
                              </span>
                              <span className="text-gray-300">|</span>
                              <span className="flex items-center gap-1">
                                <Car className="w-3 h-3 text-gray-400" />
                                <span className="font-medium text-gray-700">
                                  {vehicle.engagement.campaignSelections}
                                </span>
                                <span>Campaign selections</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* OFFERS TAB                                                        */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                FAM standing offers are open until the campaign funds or expires. Accept to initiate the proofing process.
              </p>
            </div>

            {SAMPLE_OFFERS.map((offer) => {
              const proofingVehicle: ProofingVehicle = {
                id: offer.id,
                year: parseInt(offer.vehicleName.split(' ')[0]),
                make: offer.vehicleName.split(' ')[1],
                model: offer.vehicleName.split(' ').slice(2).join(' '),
                imageUrl: offer.imageUrl,
              };
              return (
                <div
                  key={offer.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="flex items-center gap-4 p-4">
                    <img
                      src={offer.imageUrl}
                      alt={offer.vehicleName}
                      className="w-20 h-14 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-semibold text-gray-900">{offer.vehicleName}</p>
                        <OfferStatusBadge status={offer.status} />
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {offer.campaignName} &mdash; {offer.creatorName}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-base font-bold text-green-600">
                          ${offer.famOffer.toLocaleString()}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 font-medium ${offer.status === 'pending' ? 'text-amber-600' : 'text-gray-400'}`}>
                        ⏱ Expires {offer.expiresAt} · 24-hr window
                      </p>
                    </div>
                  </div>
                  <div className="px-4 pb-4">
                    {offer.status === 'pending' && (
                      <button
                        onClick={() => setProofingModalVehicle(proofingVehicle)}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Review Offer
                      </button>
                    )}
                    {offer.status === 'accepted' && (
                      <button
                        onClick={() => setProofingModalVehicle(proofingVehicle)}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Submit Documents
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* PROOFING TAB                                                      */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === 'proofing' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Vehicles selected by FAM campaign creators, pending your documentation.
            </p>
            {PROOFING_VEHICLES.map((vehicle) => (
              <div
                key={vehicle.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
              >
                <div className="flex items-center gap-4 p-4">
                  <img
                    src={vehicle.imageUrl}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-24 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">
                        Selected by creator
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      A FAM member selected your {vehicle.make} {vehicle.model} for their campaign.
                    </p>
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <button
                    onClick={() => setProofingModalVehicle(vehicle)}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
                  >
                    <FileText className="w-4 h-4" />
                    Submit Title & Documents
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* EARNINGS TAB                                                      */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === 'earnings' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* TODO: replace hardcoded values with real API data */}
              {[
                {
                  icon: Package,
                  label: 'Total Funded Vehicles',
                  value: '4',
                  color: 'text-indigo-600',
                  bg: 'bg-indigo-50',
                },
                {
                  icon: DollarSign,
                  label: 'Total Earned',
                  value: '$62,400',
                  color: 'text-green-600',
                  bg: 'bg-green-50',
                },
                {
                  icon: TrendingUp,
                  label: 'Pending Payouts',
                  value: '$18,500',
                  color: 'text-amber-600',
                  bg: 'bg-amber-50',
                },
              ].map(({ icon: Icon, label, value, color, bg }) => (
                <div
                  key={label}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
                >
                  <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>{value}</p>
                </div>
              ))}
            </div>

            {/* Payout history */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Payouts</h3>
              {/* TODO: replace with real payout transaction history */}
              <div className="space-y-2">
                {[
                  { vehicle: '2020 Toyota Camry', amount: '$18,500', date: 'Feb 14, 2026', status: 'Paid' },
                  { vehicle: '2019 Honda Accord', amount: '$16,900', date: 'Jan 28, 2026', status: 'Paid' },
                  { vehicle: '2021 Ford F-150', amount: '$27,000', date: 'Jan 10, 2026', status: 'Paid' },
                ].map((payout) => (
                  <div
                    key={payout.vehicle}
                    className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{payout.vehicle}</p>
                      <p className="text-xs text-gray-500">{payout.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">{payout.amount}</p>
                      <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                        {payout.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* BRANDING TAB                                                     */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === 'branding' && (
          <BrandingSection dealer={DEALER_PROFILE} />
        )}

      </div>

      {/* Proofing modal */}
      {proofingModalVehicle && (
        <ProofingModal
          vehicle={proofingModalVehicle}
          onClose={() => setProofingModalVehicle(null)}
        />
      )}
    </div>
  );
}
